/**
 * FlashcardCard component with flip animation
 * Displays Spanish on front, English on back
 * Mobile-responsive with touch support
 */

import { useState } from 'react'
import { FlashcardText } from './FlashcardText'
// import { WaveformPlayer } from './WaveformPlayer' // Commented out - using top-level player instead

interface FlashcardCardProps {
  frontSpanish: string[]
  backEnglish: string[]
  targetVerb?: string // Spanish verb to highlight on front
  sourceVerb?: string // English verb to highlight on back
  audioUrl?: string | null
  extractedAudioUrl?: string | null // Extracted clip URL if available
  fullSongUrl?: string | null // Full song URL for fallback
  flashcardId?: string // Unique identifier for Redux audio coordination
  onFlip?: () => void
  isFlipped?: boolean
  className?: string
}

export function FlashcardCard({
  frontSpanish,
  backEnglish,
  targetVerb,
  sourceVerb,
  // audioUrl,
  // extractedAudioUrl,
  // fullSongUrl is not used - audioUrl already contains the preferred audio (extracted or full)
  // flashcardId,
  onFlip,
  isFlipped: controlledIsFlipped,
  className = '',
}: FlashcardCardProps) {
  const [internalIsFlipped, setInternalIsFlipped] = useState(false)
  const isFlipped = controlledIsFlipped !== undefined ? controlledIsFlipped : internalIsFlipped

  // Commented out - using top-level audio player instead
  // const audioToPlay = audioUrl
  // const isExtractedClip = extractedAudioUrl !== null && extractedAudioUrl !== undefined
  // const audioId = flashcardId ? `flashcard-${flashcardId}` : audioUrl || undefined

  const handleFlip = () => {
    if (controlledIsFlipped === undefined) {
      setInternalIsFlipped(!internalIsFlipped)
    }
    onFlip?.()
  }

  return (
    <div
      className={`relative w-full h-64 sm:h-80 perspective-1000 ${className}`}
      onClick={handleFlip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleFlip()
        }
      }}
    >
      {/* Card container with 3D flip */}
      <div
        className={`relative w-full h-full preserve-3d transition-transform duration-500 ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Front side (Spanish) - Lined Notecard Style */}
        <div
          className={`absolute inset-0 w-full h-full backface-hidden rounded-sm bg-[#fefdf5] px-4 sm:px-8 flex flex-col cursor-pointer shadow-[0_8px_16px_rgba(0,0,0,0.2),_0_12px_24px_rgba(0,0,0,0.1)] ${
            isFlipped ? 'hidden' : ''
          }`}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            backgroundImage: `
              repeating-linear-gradient(
                to bottom,
                transparent 0px,
                transparent 47px,
                rgba(59, 130, 246, 0.15) 47px,
                rgba(59, 130, 246, 0.15) 48px
              ),
              linear-gradient(
                to bottom,
                transparent 0px,
                transparent 47px,
                rgba(239, 68, 68, 0.4) 47px,
                rgba(239, 68, 68, 0.4) 50px,
                transparent 50px
              )
            `,
            backgroundPosition: '0 0',
            backgroundRepeat: 'repeat-y, no-repeat',
          }}
        >
          {/* Mobile-only: Hide third blue line to prevent text intersection */}
          <div className="sm:hidden absolute left-0 right-0 bg-[#fefdf5] h-[1px]" style={{ top: '143px' }} />
          {/* Centered content area */}
          <div className="flex-1 flex flex-col items-center justify-center relative z-10 pt-8">
            <div className="text-gray-600 text-xs sm:text-sm font-sans mb-2 uppercase tracking-wider font-medium">
              Spanish
            </div>
            <div className="mt-4 sm:mt-2">
              <FlashcardText
                lines={frontSpanish}
                targetVerb={targetVerb}
                className="text-gray-900 text-xl sm:text-2xl text-center font-serif leading-[48px] align-text-top"
              />
            </div>
          </div>

          {/* Press to flip at bottom - responsive text */}
          <div className="text-gray-500 text-sm sm:text-base font-sans text-center relative z-10 pb-4 sm:pb-6">
            <span className="sm:hidden">Tap to see translation</span>
            <span className="hidden sm:inline">Click to see translation</span>
          </div>
        </div>

        {/* Back side (English) - Lined Notecard Style */}
        <div
          className={`absolute inset-0 w-full h-full backface-hidden rounded-sm bg-[#fefdf5] px-4 sm:px-8 flex flex-col cursor-pointer rotate-y-180 shadow-[0_8px_16px_rgba(0,0,0,0.2),_0_12px_24px_rgba(0,0,0,0.1)] ${
            !isFlipped ? 'hidden' : ''
          }`}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            backgroundImage: `
              repeating-linear-gradient(
                to bottom,
                transparent 0px,
                transparent 47px,
                rgba(59, 130, 246, 0.15) 47px,
                rgba(59, 130, 246, 0.15) 48px
              ),
              linear-gradient(
                to bottom,
                transparent 0px,
                transparent 47px,
                rgba(239, 68, 68, 0.4) 47px,
                rgba(239, 68, 68, 0.4) 50px,
                transparent 50px
              )
            `,
            backgroundPosition: '0 0',
            backgroundRepeat: 'repeat-y, no-repeat',
          }}
        >
          {/* Mobile-only: Hide third blue line to prevent text intersection */}
          <div className="sm:hidden absolute left-0 right-0 bg-[#fefdf5] h-[1px]" style={{ top: '143px' }} />
          {/* Centered content area */}
          <div className="flex-1 flex flex-col items-center justify-center relative z-10 pt-8">
            <div className="text-gray-600 text-xs sm:text-sm font-sans mb-2 uppercase tracking-wider font-medium">
              English
            </div>
            <div className="mt-4 sm:mt-2">
              <FlashcardText
                lines={backEnglish}
                targetVerb={sourceVerb}
                className="text-gray-900 text-xl sm:text-2xl text-center font-serif leading-[48px]"
              />
            </div>
          </div>

          {/* Press to flip at bottom - responsive text */}
          <div className="text-gray-500 text-sm sm:text-base font-sans text-center relative z-10 pb-4 sm:pb-6">
            <span className="sm:hidden">Tap to see translation</span>
            <span className="hidden sm:inline">Click to see translation</span>
          </div>
        </div>
      </div>
    </div>
  )
}

